import type { FetchError } from "../types/fetchError";
import type { ErrorResponse, OkResponse } from "../types/serviceResponse";

type Permission = { purpose: string; data: string[] };

type PermissionResponse = {
  permissions: Permissions;
};

type Permissions = Permission[];

const lbmpBaseUrl = process.env.NEXT_PUBLIC_LBMP_API_BASE;
const lbmpPublisher = process.env.NEXT_PUBLIC_LBMP_PUBLISHER;

const validateSdrn = (sdrn: string): string => {
  const pulseSdrnRegex =
    /^sdrn:[a-zA-Z0-9_.-]*:[a-zA-Z0-9_.-]*:[a-zA-Z0-9_.-]*$/;
  if (!pulseSdrnRegex.test(sdrn)) {
    throw new Error(`Invalid pulse sdrn: ${sdrn}`);
  }
  return sdrn;
};

// In-flight request deduplication
// Prevents multiple simultaneous requests to the same endpoint
const inflightRequests = new Map<
  string,
  Promise<OkResponse<PermissionResponse> | ErrorResponse>
>();

// Short-lived result cache (5 minutes)
// Caches successful results to avoid repeated network requests
interface CachedResult {
  data: OkResponse<PermissionResponse> | ErrorResponse;
  timestamp: number;
}
const resultCache = new Map<string, CachedResult>();
const RESULT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const getAnonymousPermissions = async (
  sdrn: string,
): Promise<OkResponse<PermissionResponse> | ErrorResponse> => {
  const validatedSdrn = validateSdrn(sdrn);
  const url = `${lbmpBaseUrl}/anonymous/${lbmpPublisher}/user/${validatedSdrn}/permissions`;

  const cachedResult = resultCache.get(url);
  if (cachedResult && Date.now() - cachedResult.timestamp < RESULT_CACHE_TTL) {
    return cachedResult.data;
  }

  const existingRequest = inflightRequests.get(url);
  if (existingRequest) {
    return existingRequest;
  }

  const requestPromise = (async () => {
    try {
      const response = await fetch(url, {
        headers: { "Content-Type": "application/json;charset=UTF-8" },
        cache: "default", // Use browser's default caching behavior
      });

      if (!response.ok) {
        return {
          data: null,
          error: {
            status: response.status,
            statusText: response.statusText,
            message: `Failed to fetch anonymous permissions ${response.status} ${response.statusText}`,
          },
        };
      }

      const data = await response.json();

      const result = { data, error: null };

      resultCache.set(url, { data: result, timestamp: Date.now() });
      return result;
    } catch (e) {
      const error = e as Error;
      console.error(error);
      const errorResult = {
        data: null,
        error: {
          message: error.message,
        } as FetchError,
      };

      return errorResult;
    } finally {
      inflightRequests.delete(url);
    }
  })();

  inflightRequests.set(url, requestPromise);

  return requestPromise;
};

const getUserPermissions = async (
  sdrn: string,
  sig: string,
): Promise<OkResponse<PermissionResponse> | ErrorResponse> => {
  const validatedSdrn = validateSdrn(sdrn);
  const url = `${lbmpBaseUrl}/spid/${lbmpPublisher}/user/${validatedSdrn}/permissions`;
  const cacheKey = `${url}:${sig}`;

  const cachedResult = resultCache.get(cacheKey);
  if (cachedResult && Date.now() - cachedResult.timestamp < RESULT_CACHE_TTL) {
    return cachedResult.data;
  }

  const existingRequest = inflightRequests.get(cacheKey);
  if (existingRequest) {
    return existingRequest;
  }

  const requestPromise = (async () => {
    try {
      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: `SPIDSIG ${sig}`,
        },
        cache: "default",
      });

      if (!response.ok) {
        return {
          data: null,
          error: {
            status: response.status,
            statusText: response.statusText,
            message: `Failed to fetch user permissions ${response.status} ${response.statusText}`,
          },
        };
      }

      const data = await response.json();
      const result = { data, error: null };

      resultCache.set(cacheKey, { data: result, timestamp: Date.now() });
      return result;
    } catch (e) {
      const error = e as Error;
      console.error(error);
      const errorResult = {
        data: null,
        error: {
          message: error.message,
        } as FetchError,
      };

      return errorResult;
    } finally {
      inflightRequests.delete(cacheKey);
    }
  })();

  inflightRequests.set(cacheKey, requestPromise);

  return requestPromise;
};

export { getUserPermissions, getAnonymousPermissions };
export type { Permissions };
